import { formatPercentage } from '@if/core/utils'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledPercentField,
  ControlledPercentSlider,
  Help,
  StatCard,
  useFormContainerContext,
} from '@if/mui'
import { Box, Divider, Slider, Stack, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CustomBuydownFormValues } from '../customBuydown-schema'

const RATE_BASE: Record<number, number> = { 6: 24.0, 12: 24.25, 18: 24.5, 24: 24.75, 36: 25.25 }
const PREVIEW_TERMS = Object.keys(RATE_BASE).map(Number)
const CONTRIBUTION_RATE_FACTOR = 0.25

const computePreviewRate = (term: number, contribution: number): number =>
  Math.max(0, RATE_BASE[term] - contribution * 100 * CONTRIBUTION_RATE_FACTOR)

const contributionMarks = [0, 5, 10, 15, 20].map((value) => ({ value, label: `${value}%` }))
const adminFeeMarks = [0, 3, 6].map((value) => ({ value, label: `${value}%` }))

export interface PlanFormFieldsProps {
  adminFeeRate: number
}

export const PlanFormFields = ({ adminFeeRate }: PlanFormFieldsProps) => {
  const { t } = useTranslation()
  const { methods } = useFormContainerContext<CustomBuydownFormValues>()
  const contribution = useWatch({ control: methods.control, name: 'contribution' }) ?? 0

  return (
    <Stack spacing={3} divider={<Divider />}>
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <ControlledDateOnlyPicker
            control={methods.control}
            name="activeFrom"
            label={t('customBuydown.activeFrom')}
            fullWidth
          />
          <ControlledDateOnlyPicker
            control={methods.control}
            name="expiresOn"
            label={t('customBuydown.expiresOn')}
            clearable
            fullWidth
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('customBuydown.datesHelper')}
        </Typography>
      </Stack>
      <ControlledNumberField
        control={methods.control}
        name="maxFinancingAmount"
        label={t('customBuydown.maxFinancingAmount')}
        helperText={t('customBuydown.maxHelper')}
        isMoney
        disableDecimals
        fullWidth
      />

      <Stack spacing={1}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">{t('customBuydown.contribution')}</Typography>
          <Help title={t('customBuydown.contributionTooltip')} />
        </Stack>
        <ControlledPercentField
          control={methods.control}
          name="contribution"
          label={t('customBuydown.contributionLabel')}
          decimals={2}
          required
          fullWidth
        />
        <Box sx={{ px: 1 }}>
          <ControlledPercentSlider
            control={methods.control}
            name="contribution"
            min={0.5}
            max={20}
            step={0.01}
            marks={contributionMarks}
          />
        </Box>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('customBuydown.patientRates')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5 }}>
          {PREVIEW_TERMS.map((term) => (
            <StatCard
              key={term}
              variant="outlined"
              label={t('customBuydown.termLabel', { months: term })}
              value={`${formatPercentage(computePreviewRate(term, contribution))}%`}
              caption={t('customBuydown.termRateFullTerm')}
            />
          ))}
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="subtitle2">{t('customBuydown.adminFee')}</Typography>
            <Help title={t('customBuydown.adminFeeTooltip')} />
          </Stack>
          <Typography variant="subtitle2" fontWeight="bold">
            {formatPercentage(adminFeeRate * 100)}%
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('customBuydown.adminFeeSetBy')}
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={adminFeeRate * 100}
            min={0}
            max={Math.max(6, adminFeeRate * 100)}
            marks={adminFeeMarks}
            disabled
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
          />
        </Box>
      </Stack>
    </Stack>
  )
}
